'use client';

import { useValidExposure } from '@/hooks/useValidExposure';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCampaignPageTracking } from '../../hooks/useCampaignPageTracking';
import { getCampaignTrackingCommonProperties } from '../../hooks/campaignTrackingProperties';
import type { CampaignsServerData, CapsuleArchiveItem } from '../../types';
import CapsuleArchive from './components/CapsuleArchive';
import CapsuleBanner from './components/CapsuleBanner';
import CapsuleProductRecommend, {
  isCapsuleProductRecommendEnabled,
  type CapsuleRecommendProductCollectHandler,
  type CapsuleRecommendProductTrackingHandler,
} from './components/CapsuleProductRecommend';
import CapsuleStylePreview from './components/CapsuleStylePreview';
import CapsuleStyleVenue from './components/CapsuleStyleVenue';
import CapsuleSubscription, {
  type CapsuleSubscriptionButtonName,
} from './components/CapsuleSubscription';
import { buildCapsuleArchiveAdJumpProperties } from './utils';

interface CapsulePageProps {
  serverData: CampaignsServerData;
  isAppCommunicationReady: boolean;
  inAppH5: boolean;
  onBottomContentReady?: () => void;
}

export default function CapsulePage({
  serverData,
  isAppCommunicationReady,
  inAppH5,
  onBottomContentReady,
}: CapsulePageProps) {
  const isWap = serverData.deviceType !== EPlatform.WEB;
  const adBanner = serverData.runtimeConfig.ad_banner;
  const capsuleData = serverData.runtimeConfig.capsule_data;
  const productRecommend = serverData.runtimeConfig.product_recommend;
  const hasProductRecommend = isCapsuleProductRecommendEnabled(productRecommend);
  const [productRecommendInitialLoaded, setProductRecommendInitialLoaded] = useState(false);
  const exposedBannerImagesRef = useRef<Set<string>>(new Set());
  const { trackEvent } = useReporting();
  const {
    trackPageView,
    trackPageLeave,
    trackCollectButtonClick,
    trackAdClick,
    trackAdExp,
    trackViewMoreClick,
    trackProductListPitsClick,
    trackProductListPitsExposure,
  } = useCampaignPageTracking(serverData.runtimeConfig);
  const trackStyleProductExposure = useCallback(
    (
      item: Parameters<CapsuleRecommendProductTrackingHandler>[0],
      index: number,
      tabName: string,
    ) => {
      trackProductListPitsExposure(item, index, '风格版位', tabName);
    },
    [trackProductListPitsExposure],
  );
  const trackStyleProductClick = useCallback<CapsuleRecommendProductTrackingHandler>(
    (item, index, tabName) => {
      trackProductListPitsClick(item, index, '风格版位', tabName);
    },
    [trackProductListPitsClick],
  );
  const trackStyleProductCollect = useCallback<CapsuleRecommendProductCollectHandler>(
    (item, index, collectType) => {
      trackCollectButtonClick(item, index, '风格版位', collectType);
    },
    [trackCollectButtonClick],
  );
  const trackRecommendProductExposure = useCallback<CapsuleRecommendProductTrackingHandler>(
    (item, index, tabName) => {
      trackProductListPitsExposure(item, index, '商品推荐版位', tabName);
    },
    [trackProductListPitsExposure],
  );
  const trackRecommendProductClick = useCallback<CapsuleRecommendProductTrackingHandler>(
    (item, index, tabName) => {
      trackProductListPitsClick(item, index, '商品推荐版位', tabName);
    },
    [trackProductListPitsClick],
  );
  const trackRecommendProductCollect = useCallback<CapsuleRecommendProductCollectHandler>(
    (item, index, collectType) => {
      trackCollectButtonClick(item, index, '商品推荐版位', collectType);
    },
    [trackCollectButtonClick],
  );
  const trackRecommendViewMoreClick = useCallback(() => {
    trackViewMoreClick(undefined, '商品推荐版位');
  }, [trackViewMoreClick]);
  const trackSubscriptionModuleExposure = useCallback(() => {
    try {
      trackEvent(
        {
          eventName: 'CampaignPageModule_Exp',
          properties: {
            ...getCampaignTrackingCommonProperties(serverData.runtimeConfig),
            module_name: '订阅版位',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Capsule subscription module exposure tracking error:', error);
    }
  }, [serverData.runtimeConfig, trackEvent]);
  const trackSubscriptionButtonClick = useCallback(
    (buttonName: CapsuleSubscriptionButtonName) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageBtn_Click',
            properties: {
              ...getCampaignTrackingCommonProperties(serverData.runtimeConfig),
              module_name: '订阅版位',
              btn_name: buttonName,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Capsule subscription button click tracking error:', error);
      }
    },
    [serverData.runtimeConfig, trackEvent],
  );
  const handleProductRecommendInitialLoadComplete = useCallback(() => {
    setProductRecommendInitialLoaded(true);
  }, []);
  const trackArchiveItemExposure = useCallback(
    (item: CapsuleArchiveItem, index: number) => {
      trackAdExp('往期推荐版位', '', index, buildCapsuleArchiveAdJumpProperties(item));
    },
    [trackAdExp],
  );
  const trackArchiveItemClick = useCallback(
    (item: CapsuleArchiveItem, index: number) => {
      trackAdClick('往期推荐版位', index, undefined, buildCapsuleArchiveAdJumpProperties(item));
    },
    [trackAdClick],
  );
  const { ref: bannerExposureRef } = useValidExposure({
    callback: () => {
      if (!adBanner?.image) return;
      if (exposedBannerImagesRef.current.has(adBanner.image)) return;

      exposedBannerImagesRef.current.add(adBanner.image);
      trackAdExp('顶部bannar位', '', 0, {
        pits_jump_type: '',
        pits_jump_content: '',
      });
    },
    ts: adBanner?.image || '',
  });

  useEffect(() => {
    if (isAppCommunicationReady && (!hasProductRecommend || productRecommendInitialLoaded)) {
      onBottomContentReady?.();
    }
  }, [
    hasProductRecommend,
    isAppCommunicationReady,
    onBottomContentReady,
    productRecommendInitialLoaded,
  ]);

  useEffect(() => {
    if (!isAppCommunicationReady) return;

    trackPageView();

    if (inAppH5) {
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);
    }

    return () => {
      trackPageLeave();
      if (inAppH5) {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
      }
    };
  }, [inAppH5, isAppCommunicationReady, trackPageLeave, trackPageView]);

  if (!isAppCommunicationReady || (!adBanner?.image && !capsuleData)) {
    return null;
  }

  return (
    <div className="w-full bg-white">
      {adBanner?.image ? (
        <div ref={bannerExposureRef}>
          <CapsuleBanner data={adBanner} isWap={isWap} />
        </div>
      ) : null}
      {capsuleData ? <CapsuleStylePreview data={capsuleData} isWap={isWap} /> : null}
      {capsuleData ? (
        <CapsuleStyleVenue
          data={capsuleData}
          marketingPageId={Number(serverData.runtimeConfig.marketing_page_id || 0)}
          isPreview={serverData.isPreview}
          isWap={isWap}
          adp={serverData.adp}
          onProductCollect={trackStyleProductCollect}
          onProductClick={trackStyleProductClick}
          onProductExposure={trackStyleProductExposure}
        />
      ) : null}
      {capsuleData?.subscription ? (
        <CapsuleSubscription
          data={capsuleData.subscription}
          activityType={serverData.runtimeConfig.template_type || serverData.activityType}
          isWap={isWap}
          marketingPageId={Number(serverData.runtimeConfig.marketing_page_id || 0)}
          onButtonClick={trackSubscriptionButtonClick}
          onModuleExposure={trackSubscriptionModuleExposure}
        />
      ) : null}
      {capsuleData?.archive ? (
        <CapsuleArchive
          data={capsuleData.archive}
          isWap={isWap}
          onItemClick={trackArchiveItemClick}
          onItemExposure={trackArchiveItemExposure}
        />
      ) : null}
      {hasProductRecommend && productRecommend ? (
        <div
          className={
            isWap
              ? 'contents'
              : '[&>div]:py-[20px] min-[1025px]:[&>div]:py-[25px] min-[1281px]:[&>div]:py-[30px] min-[1441px]:[&>div]:py-[40px]'
          }
          data-capsule-product-recommend
        >
          <CapsuleProductRecommend
            config={productRecommend}
            runtimeConfig={serverData.runtimeConfig}
            deviceType={serverData.deviceType}
            onProductCollect={trackRecommendProductCollect}
            onProductClick={trackRecommendProductClick}
            onProductExposure={trackRecommendProductExposure}
            onViewMoreClick={trackRecommendViewMoreClick}
            onInitialLoadComplete={handleProductRecommendInitialLoadComplete}
          />
        </div>
      ) : null}
    </div>
  );
}
